import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import type { SendDefaults, WorkOrder } from "../../api/workorders";

// 寄出工單(規格 §7):收件人自己填(廠商 2026-10-15 拿掉了;舊單有廠商 Email 的會預先帶入),主旨與內文預設由後端給,附 PDF。

// 與後端 schemas._EMAIL_RE 同一個規則
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s.]+$/;
const split = (text: string): string[] => text.split(/[,，;；\s]+/).map((s) => s.trim()).filter(Boolean);

export interface SendWorkOrderDialogProps {
  open: boolean;
  workOrderId: number;
  onClose: () => void;
  onSent: (wo: WorkOrder) => void;
}

export function SendWorkOrderDialog({ open, workOrderId, onClose, onSent }: SendWorkOrderDialogProps) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api.get<SendDefaults>(`/work-orders/${workOrderId}/send-defaults`)
      .then((d) => {
        if (!alive) return;
        setTo(d.to.join(", "));
        setCc(d.cc.join(", "));
        setSubject(d.subject);
        setBody(d.body);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : "載入失敗"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, workOrderId]);

  const send = async () => {
    if (inFlight.current) return;
    const toList = split(to);
    if (toList.length === 0) {
      setError("至少要有一位收件人");
      return;
    }
    const bad = [...toList, ...split(cc)].find((a) => !EMAIL_RE.test(a));
    if (bad) {
      setError(`「${bad}」不是正確的信箱格式`);
      return;
    }
    inFlight.current = true;
    setSending(true);
    setError(null);
    try {
      onSent(await api.post<WorkOrder>(`/work-orders/${workOrderId}/send`, { to: toList, cc: split(cc), subject, body }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "寄送失敗");
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="寄出工單"
      footer={<>
        <Button type="button" variant="secondary" disabled={sending} onClick={onClose}>取消</Button>
        <Button type="button" variant="primary" loading={sending} disabled={loading} onClick={() => void send()}>寄出</Button>
      </>}>
      <div className="flex flex-col gap-3">
        <Input label="收件人" value={to} hint="多位請用逗號隔開" onChange={(e) => setTo(e.target.value)} />
        <Input label="副本" value={cc} onChange={(e) => setCc(e.target.value)} />
        <Input label="主旨" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-[var(--text-2)]">內文</span>
          <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)}
            className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1.5 text-sm" />
        </label>
        <p className="m-0 text-xs text-[var(--text-3)]">附件:工單 PDF。由 system+承辦人標籤@nebula.com.tw 代承辦人寄出,廠商回信和備份都會到承辦人信箱;寄出的原信存在按寄出的人的信箱(「系統寄出」)。</p>
        {error ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">{error}</p> : null}
      </div>
    </Dialog>
  );
}
